import { ArrowLeft, ArrowRight, CircleOff, Info, Loader2, Map } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  BottomBar,
  BottomBarSection
} from '@bliks-io/ui';
import { SaveStatusIndicator } from './SaveStatusIndicator';

interface ProcessRecordingBottomBarProps {
  onBack: () => void;
  onAssignSomeoneElse?: () => void;
  processName?: string;
  onViewProcessDetails?: () => void;
  onViewProcessFlow?: () => void;
  isSaving: boolean;
  hasUnsavedChanges?: boolean;
  primaryAction: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    disabled?: boolean;
    icon?: ReactNode;
    /**
     * i18n keys for the validators currently failing. When provided and the
     * button is disabled, a tooltip lists them so the user knows what's missing.
     */
    disabledReasons?: string[];
    /** i18n key for the disabled-tooltip heading. Defaults to the finish copy. */
    disabledTitle?: string;
  };
  showAssignButton?: boolean;
}

export function ProcessRecordingBottomBar({
  onBack,
  onAssignSomeoneElse,
  processName,
  onViewProcessDetails,
  onViewProcessFlow,
  isSaving,
  hasUnsavedChanges,
  primaryAction,
  showAssignButton = true
}: ProcessRecordingBottomBarProps) {
  const { t } = useTranslation();

  const buttonDisabled = primaryAction.disabled || isSaving || primaryAction.loading;
  const reasons = primaryAction.disabledReasons;
  const showReasonsTooltip =
    buttonDisabled && !primaryAction.loading && reasons && reasons.length > 0;

  const finishButton = (
    <Button
      disabled={buttonDisabled}
      aria-busy={primaryAction.loading || undefined}
      onClick={primaryAction.onClick}
    >
      {primaryAction.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
      {primaryAction.label}
      {!primaryAction.loading
        ? primaryAction.icon || <ArrowRight className="w-4 h-4 ml-2" />
        : null}
    </Button>
  );

  return (
    <BottomBar className="p-4">
      <BottomBarSection>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.Back')}
        </Button>

        {showAssignButton && onAssignSomeoneElse && (
          <Button variant="outline" onClick={onAssignSomeoneElse}>
            <CircleOff className="w-4 h-4 mr-2" />
            {t('processPage.assignSomeoneElse')}
          </Button>
        )}
      </BottomBarSection>

      <BottomBarSection>
        <div className="flex items-center">
          {/* Left spacer to offset the two icon buttons on the right for visual centering */}
          <div className="w-12" />
          <div className="text-left">
            <div className="text-sm text-muted-foreground">{t('processPage.Processname')}</div>
            <div className="font-bold">{processName || t('viralRecording.status.loading')}</div>
          </div>
          <div className="flex items-center gap-1 ml-4">
            {onViewProcessDetails && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={onViewProcessDetails}>
                    <Info className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('processPage.viewProcessDetails')}</TooltipContent>
              </Tooltip>
            )}
            {onViewProcessFlow && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={onViewProcessFlow}>
                    <Map className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('processPage.processFlow')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </BottomBarSection>

      <BottomBarSection>
        <SaveStatusIndicator isSaving={isSaving} hasUnsavedChanges={hasUnsavedChanges} />
        {showReasonsTooltip ? (
          <Tooltip>
            {/* Radix Tooltip doesn't fire on natively-disabled buttons — the span receives the pointer events. */}
            <TooltipTrigger asChild>
              <span tabIndex={0}>{finishButton}</span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="font-medium">
                {t(primaryAction.disabledTitle ?? 'recording.finishDisabled.title')}
              </div>
              <ul className="mt-1 list-disc pl-4">
                {reasons.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        ) : (
          finishButton
        )}
      </BottomBarSection>
    </BottomBar>
  );
}
